// src/components/kit/microsite/MicrositeManagement.tsx

import { useNavigate } from 'react-router-dom';
import { useMicrositePage, useDeleteMicrositePage } from '@/hooks/useMicrosite';
import { useSubscriptionLimits } from '@/hooks/useSubscription';
import Spinner from '@/components/common/Spinner';
import EmptyState from '@/components/common/EmptyState';
import UpgradeModal from '@/components/common/UpgradeModal';
import { useState } from 'react';

export default function MicrositeManagement() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useMicrositePage();
  const deleteMutation = useDeleteMicrositePage();
  const { canAccessAnalytics } = useSubscriptionLimits();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // No page exists yet — show empty state
  const page = data?.page;
  if (!page || error) {
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState
          title="Create your Microsite"
          description="Build a free, customizable microsite for your music. Showcase your bio, events, links, and more — all in one beautiful page."
          icon={
            <svg className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
            </svg>
          }
          action={
            <button
              onClick={() => navigate('/app/kit/microsite/edit')}
              className="rounded-lg bg-primary-600 px-6 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-primary-700"
            >
              Create Microsite
            </button>
          }
        />
      </div>
    );
  }

  // Page exists — show management view
  const handleDelete = async () => {
    await deleteMutation.mutateAsync();
    setShowDeleteConfirm(false);
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(page.public_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAnalyticsClick = () => {
    if (canAccessAnalytics) {
      navigate('/app/kit/microsite/analytics');
    } else {
      setShowUpgradeModal(true);
    }
  };

  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-6">
      {/* Header */}
      <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-neutral-800">Microsite</h1>
          <p className="mt-1 text-sm text-neutral-500">Manage your public microsite</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleAnalyticsClick}
            className="rounded-lg border border-neutral-300 bg-neutral-200 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-300"
          >
            Analytics
          </button>
          <button
            onClick={() => navigate('/app/kit/microsite/edit')}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-black transition-colors hover:bg-primary-700"
          >
            Edit Page
          </button>
        </div>
      </div>

      {/* Page card */}
      <div className="rounded-xl border border-neutral-300 bg-neutral-200 p-4 sm:p-6">
        <div className="flex items-start gap-4">
          {page.profile_image_url ? (
            <img src={page.profile_image_url} alt={page.artist_name} className="h-16 w-16 flex-shrink-0 rounded-full object-cover" />
          ) : (
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-neutral-300">
              <svg className="h-8 w-8 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-lg font-semibold text-neutral-800">{page.artist_name}</h2>
            {page.tagline && <p className="mt-0.5 truncate text-sm text-neutral-500">{page.tagline}</p>}
            <div className="mt-2 flex items-center gap-3">
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${page.published ? 'bg-green-900/30 text-green-400' : 'bg-neutral-300 text-neutral-500'}`}>
                {page.published ? 'Published' : 'Draft'}
              </span>
              <span className="text-xs text-neutral-500">{page.links.length} link{page.links.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>

        {/* Public URL */}
        <div className="mt-4 rounded-lg border border-neutral-300 bg-neutral-100 p-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-xs text-neutral-500">Public URL</p>
              <p className="mt-0.5 break-all text-sm font-medium text-primary-600">{page.public_url}</p>
            </div>
            <button onClick={handleCopyUrl} className="flex-shrink-0 self-start rounded-md border border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-300 sm:self-auto">
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        </div>

        {/* Quick stats */}
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-lg border border-neutral-300 bg-neutral-100 p-3 text-center">
            <p className="text-xs text-neutral-500">Layout</p>
            <p className="mt-0.5 text-sm font-medium capitalize text-neutral-700">{page.layout}</p>
          </div>
          <div className="rounded-lg border border-neutral-300 bg-neutral-100 p-3 text-center">
            <p className="text-xs text-neutral-500">Palette</p>
            <p className="mt-0.5 text-sm font-medium capitalize text-neutral-700">{page.palette_id}</p>
          </div>
          <div className="rounded-lg border border-neutral-300 bg-neutral-100 p-3 text-center">
            <p className="text-xs text-neutral-500">Font Theme</p>
            <p className="mt-0.5 text-sm font-medium capitalize text-neutral-700">{page.font_theme_id}</p>
          </div>
        </div>
      </div>

      {/* Danger zone */}
      <div className="mt-8 rounded-xl border border-red-900/30 bg-red-900/10 p-4 sm:p-6">
        <h3 className="text-sm font-semibold text-red-400">Danger Zone</h3>
        <p className="mt-1 text-sm text-neutral-500">Permanently delete your microsite and all associated data.</p>
        {showDeleteConfirm ? (
          <div className="mt-3 flex items-center gap-2">
            <button onClick={handleDelete} disabled={deleteMutation.isPending} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-500">
              {deleteMutation.isPending ? 'Deleting...' : 'Confirm Delete'}
            </button>
            <button onClick={() => setShowDeleteConfirm(false)} className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-300">Cancel</button>
          </div>
        ) : (
          <button onClick={() => setShowDeleteConfirm(true)} className="mt-3 rounded-lg border border-red-600/50 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-900/20">Delete Microsite</button>
        )}
      </div>

      {/* Upgrade modal for analytics */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </div>
  );
}
